import React from 'react';
import type { RentalAssetResult, RentalDispatchLoadOutcome } from '../../types/rental-dispatch-complete';

/**
 * What a delivery or return did to each asset, as the API reported it
 * (api#945). A Fail names its work order. `rate_missing` is deliberately NOT
 * shown: the tech cannot fix pricing, and the office sees it on the order and
 * billing screens.
 */
const LINE: Record<RentalAssetResult['outcome'], (r: RentalAssetResult) => string> = {
  on_rent: () => 'On rent',
  failed_inspection: (r) =>
    `Failed inspection. Work order ${r.work_order_number ?? '—'}, sent to maintenance${r.period_id ? '' : ', not put on rent'}`,
  in_maintenance: () => 'Already in maintenance, not put on rent',
  returned: () => 'Off rent, back to the yard',
  returned_in_maintenance: () => 'Off rent, stays in maintenance',
};

export default function DispatchOutcomeList({ assets }: { assets: RentalAssetResult[] }) {
  if (assets.length === 0) return null;
  return (
    <ul data-testid="dispatch-outcomes" className="w-full max-w-xs space-y-2 mb-6">
      {assets.map((r) => (
        <li
          key={r.asset_id}
          data-testid="dispatch-outcome"
          className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3"
        >
          <p className="text-sm font-semibold text-white">{r.asset_number}</p>
          <p className={`text-xs ${r.outcome === 'failed_inspection' || r.outcome === 'returned_in_maintenance' || r.outcome === 'in_maintenance' ? 'text-amber-400' : 'text-fieldiq-muted'}`}>
            {(LINE[r.outcome] ?? (() => '—'))(r)}
          </p>
        </li>
      ))}
    </ul>
  );
}

/**
 * What the delivery or return did to the dispatch's trucking load (api#1050),
 * one line. A load that was already delivered, signed or cancelled is
 * information, not an error: nothing was changed. No load (a rentals-only
 * tenant, or none was made) and a replay say nothing.
 */
export function DispatchLoadNote({ load }: { load: RentalDispatchLoadOutcome | null | undefined }) {
  if (!load) return null;
  const text =
    load.status === 'closed' ? 'Trucking load closed as delivered.'
    : load.status === 'cancelled' ? 'Trucking load cancelled.'
    : load.status === 'left' ? `Trucking load already ${String(load.load_status || 'closed').replace(/_/g, ' ')}; not changed.`
    : null;
  if (!text) return null;
  return <p data-testid="dispatch-load-note" className="mb-4 max-w-xs text-center text-xs text-fieldiq-muted">{text}</p>;
}
